// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, React FOUNDATION typed fetch client.
//
// A tiny typed wrapper over fetch that attaches authHeaders() and parses JSON,
// mirroring the request conventions the legacy desktop wiring uses against the
// live workers:
//
//   • AUTH_WORKER  = https://auth.cntrlglobal.com   (dashboard-wiring.js:20)
//   • STATS_WORKER = https://stats.cntrlglobal.com  (release-focused-wiring.js:32,
//                    connectors-wiring.js:25, settings-wiring.js:29, …)
//
// The legacy /api/me + /api/data/* reads send `headers: authHeaders(false)`
// (no Content-Type, just Bearer) for GETs, and writes send
// `headers: authHeaders(true)` (Bearer + Content-Type). apiGet/apiPost here
// reproduce that exactly via lib/auth.authHeaders(method).
// ─────────────────────────────────────────────────────────────────────────────

import { authHeaders, getCurrentBandId, getToken, clearToken } from './auth';
/* jedi-view-as-central 2026-07-28 — see applyViewAsToMe below. viewAs.ts
   imports only ./types, so this adds no import cycle. */
import { applyViewAs } from './viewAs';
import type { User } from './types';

/** Base URL of the main auth/data worker. Mirrors dashboard-wiring.js AUTH_WORKER.
 *
 * staging-lane 2026-07-06: overridable via localStorage so ANY build can be
 * pointed at the staging worker without a separate bundle. In the console:
 *   localStorage.setItem('ac:api:base', 'https://cntrl-auth-staging.<acct>.workers.dev'); location.reload()
 * and back:  localStorage.removeItem('ac:api:base'); location.reload()
 * No-op for everyone who never sets the key. */
export const AUTH_WORKER = ((): string => {
  try {
    const o = localStorage.getItem('ac:api:base');
    if (o) return o.replace(/\/+$/, '');
  } catch { /* SSR / storage disabled, fall through */ }
  /* local-staging 2026-07-20 - when the app itself is served from localhost
     (vite dev / vite preview), default to the local worker (wrangler dev on
     :8787) instead of production. The ac:api:base override above still wins
     when set - point it at the deployed staging worker to test
     frontend-only changes without running the worker locally. Production
     builds are untouched: their hostname is never localhost. */
  try {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:8787';
  } catch { /* non-browser context - fall through */ }
  return 'https://auth.cntrlglobal.com';
})();

/** Base URL of the stats/analytics worker. Mirrors *-wiring.js STATS_WORKER. */
export const STATS_WORKER = 'https://stats.cntrlglobal.com';

/** Which worker a request targets. */
export type WorkerName = 'auth' | 'stats';

const BASE_URLS: Record<WorkerName, string> = {
  auth: AUTH_WORKER,
  stats: STATS_WORKER,
};

/**
 * Thrown when a worker request fails (non-2xx, network error, or bad JSON).
 * Carries the HTTP status (0 for network/parse failures) and the resolved URL
 * so callers can branch on auth (401) vs reachability (403) vs everything else.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly body: string | null;

  constructor(message: string, status: number, url: string, body: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

function resolveUrl(worker: WorkerName, path: string): string {
  const base = BASE_URLS[worker];
  if (/^https?:\/\//i.test(path)) return path; // already absolute
  return base + (path.startsWith('/') ? path : '/' + path);
}

// auth-401 2026-07-06: a 401 means the session is gone (expired, or killed by a
// password reset which deletes all sessions). Without this, the app stays fully
// chromed while every screen silently renders empty. On the FIRST 401 we clear
// the token and reload → the boot gate lands on the login screen. Guarded so a
// burst of parallel 401s triggers exactly one reload; skipped for the auth
// endpoints themselves (login/otp legitimately 401 on bad creds, those callers
// handle it inline and must NOT nuke the session).
let _handling401 = false;
function _isAuthEndpoint(url: string): boolean {
  var p = path(url);
  return p.indexOf('/api/login') === 0 || p.indexOf('/api/otp') === 0 ||
         p.indexOf('/api/signup') === 0 || p.indexOf('/api/password') === 0 ||
         p.indexOf('/api/webauthn') === 0 || p.indexOf('/api/passkey') === 0;
}
function _onUnauthorized(url: string): void {
  if (_handling401 || _isAuthEndpoint(url) || !getToken()) return;
  /* 401-scope fix 2026-07-06: only the AUTH worker is the authority on whether
     the session is dead. A 401 from the stats worker (transient failure,
     misconfig, or an env split like staging-auth + prod-stats) must NOT nuke
     the session and bounce the user to login, that's a stats problem, not a
     session problem. Found live: staging rehearsal logged users out because
     prod-stats correctly rejected a staging session token. */
  if (!url.startsWith(AUTH_WORKER)) return;
  /* 401-scope fix, part 2 (staging rehearsal find, 2026-07-06): the auth
     worker's THIRD-PARTY proxy endpoints (/api/proxy/shopify-summary etc.)
     return 401 when the DOWNSTREAM platform rejects its token, e.g. an
     expired Shopify connection. That is "reconnect Shopify", NOT "session
     dead": nuking the session here bounce-loops the user out of the app the
     moment their dashboard loads. This is live in prod for any user whose
     platform token expires, staging surfaced it because it has no Shopify
     secrets at all. */
  if (url.indexOf('/api/proxy/') !== -1) return;
  _handling401 = true;
  void clearToken().finally(function () {
    try { localStorage.removeItem('cntrl_auth_token'); } catch (e) { /* ignore */ }
    location.reload();
  });
}

async function parseJson<T>(res: Response, url: string): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    if (res.status === 401) _onUnauthorized(url);
    throw new ApiError('Request failed: ' + res.status + ' ' + path(url), res.status, url, text || null);
  }
  if (!text) {
    // Some endpoints (rare) return an empty 200, treat as null payload.
    return null as unknown as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError('Invalid JSON response from ' + path(url), 0, url, text);
  }
}

/** Strip the origin for tidy error messages. */
function path(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

// ── In-flight GET dedupe + /api/me TTL memo, audit 2026-07-02 perf ──────────
//
// The same GET (notably /api/data/schedule and /api/me) fires 3-4× in parallel
// per screen mount, because independent hooks/sections each kick off their own
// read. Mirrors the legacy site/js/fetch-dedupe.js: while a GET is in flight,
// every concurrent identical GET shares the SAME network request. Each caller
// receives its own Response CLONE (fresh body stream → independently parsed
// JSON), so no two callers ever share a mutable parsed object, same guarantee
// as today's independent fetches.
//
// The dedupe key folds in the active band context + a short bearer-token
// fingerprint (fetch-dedupe.js CRIT-14 / Bible Rules 1-2): the same URL under a
// different silo or token must NEVER collapse into one request and clone the
// other band's response.
//
// Only GETs are ever deduped, apiPost/apiPut below always hit the wire.
// Nothing is cached past settle: the in-flight entry is dropped the moment the
// request resolves OR rejects, so errors are never served to later callers.
const _inflightGets = new Map<string, Promise<Response>>();

// /api/me additionally gets a short TTL memo: it is effectively constant for a
// session and re-read by many mounts in quick succession. ONLY /api/me, no
// other GET is ever memoized. Non-2xx and network errors are never memoized.
const ME_MEMO_TTL_MS = 15_000;
let _meMemo: { key: string; at: number; res: Promise<Response> } | null = null;

/**
 * Drop the /api/me memo, audit 2026-07-02 perf.
 * Called automatically on every auth-worker write below (profile saves go
 * through apiPut('auth', '/api/me/profile', …) → lib/settings.ts,
 * MobileProfile), so a post-save /api/me read is always fresh. Band switches
 * self-bust without calling this: the memo key folds in getCurrentBandId(), so
 * a different band context can never read a stale entry. Exported for any
 * surface that mutates identity outside this module (e.g. raw-fetch profile
 * modals) and wants an explicit bust.
 */
export function bustMeCache(): void {
  _meMemo = null;
}

/** Dedupe key: URL + band silo + token fingerprint (last 8 chars, enough to
 *  discriminate tokens without spilling the secret into map keys). */
function dedupeKey(url: string): string {
  const t = getToken();
  return url + ' b=' + getCurrentBandId() + ' t=' + (t.length > 8 ? t.slice(-8) : t);
}

/** The one raw GET, everything above it is coalescing, not caching. */
function rawGet(url: string): Promise<Response> {
  return fetch(url, {
    method: 'GET',
    headers: authHeaders('GET'),
    cache: 'no-store',
  });
}

/* ── Boot batch prime (perf-tranche 2026-07-14, Dev consult: consolidation) ──
   The worker has served GET /api/data?keys=… (handleBatchGetUserData) since
   the 2026-06-03 audit, but this app never called it, boot fired six
   singular /api/data/<key> GETs (~200-260ms each, mostly per-request
   overhead). primeDataBatch() makes ONE batch request and seeds this map;
   dedupedGet serves /api/data/<key> reads from it for a short window,
   synthesizing the EXACT singular response shape ({success, key, data}) so
   consumers can't tell the difference. Silo rules match the in-flight
   dedupe: entries are keyed by URL + band + token fingerprint, so a band
   switch or re-auth can never read across. The TTL is short, this
   collapses BOOT fan-out; later SWR revalidations hit the wire as before.
   Keys the batch endpoint drops (impersonation allowlist, unknown keys)
   simply aren't primed, the singular read fires with its own gates. */
const DATA_PRIME_TTL_MS = 15_000;
const _primedData = new Map<string, { at: number; json: string }>();
let _primeInflight: Promise<void> | null = null;

export function primeDataBatch(keys: ReadonlyArray<string>): Promise<void> {
  if (_primeInflight) return _primeInflight;
  const url = resolveUrl('auth', '/api/data?keys=' + keys.join(','));
  const p = (async (): Promise<void> => {
    try {
      const res = await rawGet(url);
      if (!res.ok) return;
      const body = (await res.json()) as { success?: boolean; data?: Record<string, unknown> };
      if (!body || body.success === false || !body.data) return;
      const at = Date.now();
      for (const k of keys) {
        if (!Object.prototype.hasOwnProperty.call(body.data, k)) continue;
        const singular = JSON.stringify({ success: true, key: k, data: body.data[k] ?? {} });
        _primedData.set(dedupeKey(resolveUrl('auth', '/api/data/' + k)), { at, json: singular });
      }
    } catch { /* best-effort, singular reads still work exactly as before */ }
  })();
  _primeInflight = p;
  void p.finally(() => { setTimeout(() => { if (_primeInflight === p) _primeInflight = null; }, 1000); });
  return p;
}

/** Drop every primed entry (band switch safety is already in the key; this is
 *  for writes, a PUT to /api/data/<key> must never be followed by a stale
 *  primed read). Called from apiPut below.
 *
 *  EXPORTED 2026-08-04 (acpro-busstock) for the read-merge-write path: a
 *  many-writer blob must re-read the CURRENT server copy immediately before
 *  it PUTs, and a boot-primed entry up to 15s old would hand it a stale base
 *  and silently drop whatever a bunkmate added in that window. Callers doing
 *  a merge write must bust first; plain readers must not (that's what the
 *  prime is for). */
export function bustDataPrime(): void {
  _primedData.clear();
}

function dedupedGet(url: string): Promise<Response> {
  const key = dedupeKey(url);
  const isMe = path(url) === '/api/me';

  // Batch-primed hit (fresh, same silo+token): serve the synthesized
  // singular response, no wire call.
  const primed = _primedData.get(key);
  if (primed) {
    if (Date.now() - primed.at < DATA_PRIME_TTL_MS) {
      return Promise.resolve(new Response(primed.json, { status: 200, headers: { 'Content-Type': 'application/json' } }));
    }
    _primedData.delete(key);
  }

  // /api/me memo hit (fresh, same silo+token), serve a clone, no wire call.
  if (isMe && _meMemo && _meMemo.key === key && Date.now() - _meMemo.at < ME_MEMO_TTL_MS) {
    return _meMemo.res.then((res) => res.clone());
  }

  // In-flight hit, share the request, hand this caller its own body clone.
  const existing = _inflightGets.get(key);
  if (existing) return existing.then((res) => res.clone());

  const p = rawGet(url);
  _inflightGets.set(key, p);
  // Dedupe only WHILE in flight (fetch-dedupe.js parity): drop the entry on
  // settle, resolve or reject, so the next caller fires a fresh request and
  // errors are never coalesced onto later callers.
  const settle = (): void => {
    if (_inflightGets.get(key) === p) _inflightGets.delete(key);
  };
  p.then(settle, settle);

  if (isMe) {
    const memo = { key, at: Date.now(), res: p };
    _meMemo = memo;
    // Never memoize failures: a rejected fetch OR a non-2xx /api/me (e.g. 401)
    // clears the memo immediately so the next read retries the wire.
    p.then(
      (res) => {
        if (!res.ok && _meMemo === memo) _meMemo = null;
      },
      () => {
        if (_meMemo === memo) _meMemo = null;
      },
    );
  }

  // The stored promise resolves to the PRISTINE (never-read) Response; every
  // caller, including the first, parses its own clone.
  return p.then((res) => res.clone());
}

/**
 * GET a JSON resource from the given worker.
 *
 * Mirrors the legacy GET convention: `headers: authHeaders(false)` (Bearer-only,
 * no Content-Type) and `cache: 'no-store'`, the worker is the only source of
 * truth and the desktop app never caches data reads client-side
 * (dashboard-wiring.js header comment "No localStorage reads/writes for user
 * data or connections, server is the only place that knows…").
 *
 * audit 2026-07-02 perf: concurrent identical GETs are coalesced onto one
 * network request (see dedupedGet above); response bodies stay per-caller.
 */
/* jedi-view-as-central 2026-07-28 (Ryan: "when you select view as role, the
   home or nothing changes") — the view-as simulator only ever worked on
   surfaces that read the user through lib/dashboard.fetchCurrentUser (the tab
   bar, via useMobileRole). EVERY mobile screen — MobileDashboard included —
   calls apiGet('auth', '/api/me') directly, so they got the RAW row and the
   simulated seat never reached the home screen's own effectiveRole().

   Applying the override here, at the single parse point, makes every
   apiGet-based /api/me consumer honour the switcher at once. It is a strict
   no-op for everyone else: applyViewAs fails closed unless the caller IS the
   artist jedi (email gate) AND an override is set, so standard AC is
   untouched. Idempotent, so lib/dashboard's second apply stays harmless.

   NOT covered (they bypass apiGet with their own fetch/api helpers):
   MobileAssets, MobileShows, MobileSpouse. Worth folding in if the simulator
   needs to drive those screens too. */
function applyViewAsToMe<T>(json: T): T {
  try {
    if (!json || typeof json !== 'object') return json;
    const envelope = json as { user?: User };
    if (envelope.user) {
      const swapped = applyViewAs(envelope.user);
      return swapped === envelope.user ? json : ({ ...envelope, user: swapped } as unknown as T);
    }
    const bare = json as unknown as User;
    const swapped = applyViewAs(bare);
    return swapped === bare ? json : (swapped as unknown as T);
  } catch {
    return json; /* the simulator must never break a real /api/me read */
  }
}

export async function apiGet<T>(worker: WorkerName, path: string): Promise<T> {
  const url = resolveUrl(worker, path);
  let res: Response;
  try {
    res = await dedupedGet(url);
  } catch (e) {
    throw new ApiError('Network error: ' + (e instanceof Error ? e.message : String(e)), 0, url);
  }
  const json = await parseJson<T>(res, url);
  return path === '/api/me' ? applyViewAsToMe(json) : json;
}

/**
 * POST a JSON body to the given worker and parse the JSON response.
 *
 * Mirrors the legacy write convention: `method: 'POST'`, `headers:
 * authHeaders()` (Bearer + Content-Type: application/json), JSON-stringified
 * body, e.g. dashboard-wiring.js fetchStats() POSTing to /api/proxy/stats and
 * fetchMerchSpotlight() POSTing to /api/proxy/shopify-summary.
 */
export async function apiPost<T>(worker: WorkerName, path: string, body?: unknown): Promise<T> {
  const url = resolveUrl(worker, path);
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: authHeaders('POST'),
      cache: 'no-store',
      body: JSON.stringify(body ?? {}),
    });
  } catch (e) {
    throw new ApiError('Network error: ' + (e instanceof Error ? e.message : String(e)), 0, url);
  }
  // audit 2026-07-02 perf, any auth-worker write may change /api/me-derived
  // state (profile save, band mutation); drop the memo so the next read is live.
  if (worker === 'auth') { bustMeCache(); bustDataPrime(); }
  return parseJson<T>(res, url);
}

/**
 * DELETE, first consumer: removing an Antenna announcement
 * (ann-rules 2026-07-11). Mirrors apiPost's conventions (no body).
 */
export async function apiDelete<T>(worker: WorkerName, path: string): Promise<T> {
  const url = resolveUrl(worker, path);
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'DELETE',
      headers: authHeaders('DELETE'),
      cache: 'no-store',
    });
  } catch (e) {
    throw new ApiError('Network error: ' + (e instanceof Error ? e.message : String(e)), 0, url);
  }
  if (worker === 'auth') { bustMeCache(); bustDataPrime(); }
  return parseJson<T>(res, url);
}

/**
 * PUT a JSON body, used by the legacy app for data writes
 * (e.g. /api/data/opportunities, /api/data/schedule with `{ value: [...] }`).
 * Included for completeness so the data layer can write back like the current
 * app does. Mirrors authHeaders(true) + JSON.stringify.
 */
export async function apiPut<T>(worker: WorkerName, path: string, body?: unknown): Promise<T> {
  const url = resolveUrl(worker, path);
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'PUT',
      headers: authHeaders('PUT'),
      cache: 'no-store',
      body: JSON.stringify(body ?? {}),
    });
  } catch (e) {
    throw new ApiError('Network error: ' + (e instanceof Error ? e.message : String(e)), 0, url);
  }
  // audit 2026-07-02 perf, see apiPost: writes bust the /api/me memo
  // (+ the boot data prime, so a save is never followed by a stale read).
  if (worker === 'auth') { bustMeCache(); bustDataPrime(); }
  return parseJson<T>(res, url);
}
